import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, TimerReset, Play, Pause, RotateCcw, ListChecks, CheckCircle2, BellRing } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";

const MIN_WORDS = /(\d+(?:[.,]\d+)?)\s*(?:–|-|a|to|à|até)?\s*(\d+)?\s*(?:min\b|minutos?|minutes?|minuti|mn\b)/i;
const HOUR_WORDS = /(\d+)\s*h(?:ora?s?|eures?|ours?|ore)?\s*(\d+)?/i;

export const detectMinutes = (text) => {
  const h = HOUR_WORDS.exec(text || "");
  if (h) return Number(h[1]) * 60 + (h[2] ? Number(h[2]) : 0);
  const m = MIN_WORDS.exec(text || "");
  if (!m) return null;
  return Number(m[2] || m[1].replace(",", "."));
};

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.35, 0.7].forEach((at) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination); o.frequency.value = 880; g.gain.value = 0.15;
      o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + 0.25);
    });
  } catch { /* no audio */ }
};

const useWakeLock = (active) => {
  useEffect(() => {
    if (!active || !navigator.wakeLock) return undefined;
    let lock;
    navigator.wakeLock.request("screen").then((l) => { lock = l; }).catch(() => {});
    return () => lock?.release?.();
  }, [active]);
};

const Timer = ({ suggested, t }) => {
  const [total, setTotal] = useState(0);
  const [left, setLeft] = useState(0);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setTotal(0); setLeft(0); setRunning(false); setDone(false); }, [suggested]);
  useEffect(() => {
    if (!running) return undefined;
    ref.current = setInterval(() => setLeft((l) => {
      if (l <= 1) { clearInterval(ref.current); setRunning(false); setDone(true); beep(); return 0; }
      return l - 1;
    }), 1000);
    return () => clearInterval(ref.current);
  }, [running]);

  const start = (mins) => { setTotal(mins * 60); setLeft(mins * 60); setDone(false); setRunning(true); };
  const pct = total ? Math.round(((total - left) / total) * 100) : 0;

  if (done) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-xmas-gold bg-xmas-gold/15 px-5 py-4 animate-pulse" data-testid="cook-timer-done">
        <BellRing className="h-6 w-6 text-xmas-gold" />
        <span className="font-display text-xl text-xmas-gold">{t("cook.timer_done")}</span>
        <button onClick={() => setDone(false)} className="ml-auto rounded-full border border-xmas-gold/40 px-3 py-1 text-xs text-xmas-gold" data-testid="cook-timer-dismiss">OK</button>
      </div>
    );
  }
  if (!total) {
    return (
      <div className="flex flex-wrap items-center gap-3" data-testid="cook-timer-idle">
        {suggested && (
          <button onClick={() => start(suggested)} className="btn-gold inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-bold" data-testid="cook-timer-start">
            <TimerReset className="h-4 w-4" /> {t("cook.timer_start", { m: suggested })}
          </button>
        )}
        <div className="flex items-center gap-2 text-sm text-xmas-slate">
          <span>{t("cook.timer_custom")}:</span>
          {[1, 5, 10, 15, 30].map((m) => (
            <button key={m} onClick={() => start(m)} className="glass rounded-full px-3 py-1.5 text-xs text-xmas-snow hover:text-xmas-gold" data-testid={`cook-timer-preset-${m}`}>{m} {t("cook.minutes")}</button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-xmas-gold/25 bg-xmas-navy/60 px-5 py-4" data-testid="cook-timer-running">
      <div className="flex items-center gap-4">
        <span className="font-mono-num text-4xl text-xmas-gold" data-testid="cook-timer-value">{fmt(left)}</span>
        <div className="ml-auto flex gap-2">
          <button onClick={() => setRunning((r) => !r)} className="btn-gold inline-flex items-center gap-1 rounded-full px-4 py-2 text-xs font-bold" data-testid="cook-timer-toggle">
            {running ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />} {running ? t("cook.timer_pause") : t("cook.timer_resume")}
          </button>
          <button onClick={() => { setRunning(false); setTotal(0); setLeft(0); }} className="glass inline-flex items-center gap-1 rounded-full px-4 py-2 text-xs text-xmas-snow" data-testid="cook-timer-reset">
            <RotateCcw className="h-3.5 w-3.5" /> {t("cook.timer_reset")}
          </button>
        </div>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-xmas-navy"><div className="h-full bg-xmas-gold transition-[width] duration-1000" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

export const CookMode = ({ recipe, onClose }) => {
  const { t } = useI18n();
  const [i, setI] = useState(0);
  const [showIngr, setShowIngr] = useState(false);
  const steps = recipe.steps || [];
  const total = steps.length;
  const finished = i >= total;
  useWakeLock(true);

  const go = useCallback((d) => setI((x) => Math.max(0, Math.min(total, x + d))), [total]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [go, onClose]);

  const suggested = finished ? null : detectMinutes(steps[i]);

  return createPortal(
    <div className="fixed inset-0 z-[200] flex flex-col bg-xmas-navy text-xmas-snow" data-testid="cook-mode">
      <header className="flex items-center gap-3 border-b border-xmas-gold/15 px-4 py-3 sm:px-8">
        <div className="min-w-0">
          <p className="truncate font-display text-lg text-xmas-gold sm:text-xl">{recipe.name}</p>
          {!finished && <p className="text-xs text-xmas-slate" data-testid="cook-step-counter">{t("cook.step_of", { n: i + 1, total })}</p>}
        </div>
        <button onClick={() => setShowIngr((s) => !s)} className={`ml-auto inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs ${showIngr ? "btn-gold font-bold" : "glass text-xmas-snow"}`} data-testid="cook-ingredients-toggle">
          <ListChecks className="h-4 w-4" /> <span className="hidden sm:inline">{t("cook.ingredients")}</span>
        </button>
        <button onClick={onClose} aria-label={t("cook.close")} className="glass rounded-full p-2 text-xmas-snow hover:text-xmas-gold" data-testid="cook-close">
          <X className="h-5 w-5" />
        </button>
      </header>

      <div className="h-1 bg-xmas-navycard"><div className="h-full bg-xmas-gold transition-[width] duration-500" style={{ width: `${(Math.min(i, total) / total) * 100}%` }} data-testid="cook-progress" /></div>

      <main className="flex flex-1 flex-col overflow-y-auto px-6 py-8 sm:px-12 lg:px-24">
        {showIngr && (
          <ul className="mb-8 grid grid-cols-1 gap-2 rounded-2xl border border-xmas-gold/15 bg-xmas-navycard p-5 sm:grid-cols-2" data-testid="cook-ingredients">
            {(recipe.ingredients || []).map((ing, k) => (
              <li key={k} className="flex items-center gap-3 text-sm sm:text-base"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-xmas-gold" />{ing}</li>
            ))}
          </ul>
        )}
        {finished ? (
          <div className="m-auto flex flex-col items-center text-center" data-testid="cook-finished">
            <CheckCircle2 className="mb-4 h-16 w-16 text-xmas-gold" />
            <h2 className="font-display text-3xl sm:text-4xl">{t("cook.done_title")}</h2>
            <p className="mt-2 text-xmas-slate">{t("cook.done_body")}</p>
            <button onClick={onClose} className="btn-gold mt-8 rounded-full px-8 py-3 text-sm font-bold uppercase tracking-wider" data-testid="cook-finish-close">{t("cook.close")}</button>
          </div>
        ) : (
          <div className="m-auto w-full max-w-4xl">
            <span className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-full bg-xmas-gold/20 font-mono-num text-xl text-xmas-gold">{i + 1}</span>
            <p key={i} className="animate-in fade-in slide-in-from-bottom-2 font-display text-2xl leading-snug sm:text-4xl lg:text-5xl" data-testid="cook-step-text">{steps[i]}</p>
            <div className="mt-10"><Timer suggested={suggested} t={t} /></div>
          </div>
        )}
      </main>

      <footer className="flex items-center gap-3 border-t border-xmas-gold/15 px-4 py-4 sm:px-8">
        <button onClick={() => go(-1)} disabled={i === 0} className="glass inline-flex items-center gap-2 rounded-full px-6 py-4 text-sm font-semibold text-xmas-snow disabled:opacity-30" data-testid="cook-prev">
          <ChevronLeft className="h-5 w-5" /> {t("cook.prev")}
        </button>
        <span className="hidden flex-1 text-center text-xs text-xmas-slate sm:block">{t("cook.hint")}</span>
        {!finished && (
          <button onClick={() => go(1)} className="btn-gold ml-auto inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wider" data-testid="cook-next">
            {i === total - 1 ? t("cook.finish") : t("cook.next")} <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </footer>
    </div>,
    document.body
  );
};
